import {
  Archive,
  ArrowRight,
  Bell,
  Briefcase,
  Building2,
  Calendar,
  Check,
  CheckCircle,
  ChevronDown,
  Clock,
  FileText,
  Filter,
  LayoutDashboard,
  Mail,
  MapPin,
  MessageSquare,
  Mic,
  MoreHorizontal,
  Paperclip,
  Phone,
  Play,
  Plus,
  Search,
  Settings,
  TrendingUp,
  Users,
  X,
} from "lucide-react";
import { AVATARS, BOARD, WAVE } from "../../data/landingPageSamples";
import { BRAND, BRAND_DARK, INK, LOGO_SRC } from "./ui";

function BoardCard({ card, index }) {
  const av = AVATARS[index % AVATARS.length];
  const warn = !!card.warn;
  return (
    <div
      className={`mb-2.5 overflow-hidden border rounded-lg shadow-sm ${
        warn ? "border-yellow-300 bg-yellow-50" : "border-slate-200 bg-white"
      }`}
    >
      {warn && (
        <div className="flex items-center gap-1 px-3 py-1 text-[9.5px] font-semibold text-yellow-800 bg-yellow-100 border-b border-yellow-300">
          <Clock size={9} /> {card.warn}
        </div>
      )}
      <div className="flex items-center gap-2 px-3 pt-3">
        <div
          className="flex items-center justify-center flex-shrink-0 text-[10.5px] font-bold rounded-md w-8 h-8"
          style={{ background: av.bg, color: av.color }}
        >
          {card.co.substring(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold truncate text-slate-900">{card.co}</div>
          <div className="text-[10px] truncate text-slate-400">{card.person}</div>
        </div>
        <MoreHorizontal size={13} className="text-slate-300" />
      </div>
      <div className="px-3 pt-2.5 space-y-1.5">
        <div className="flex items-center gap-1.5 text-[10.5px] text-slate-500">
          <Phone size={10} className="text-slate-300" /> {card.phone}
        </div>
        <div className="flex items-center gap-1.5 text-[10.5px] text-slate-500">
          <Briefcase size={10} className="text-slate-300" /> {card.industry}
        </div>
        <div className="text-[10px] font-semibold text-slate-500">
          {card.deals ? `› ${card.deals}` : "› 0 deals"}
        </div>
      </div>
      <div className="flex items-center justify-between px-3 py-2 mt-2.5 border-t border-slate-100 bg-slate-50">
        <div className="flex items-center gap-1.5">
          <span
            className="flex items-center justify-center w-5 h-5 text-[8px] font-bold text-white rounded-full"
            style={{ background: BRAND }}
          >
            {card.rep}
          </span>
          <span className="text-[9.5px] text-slate-400">{card.ago}</span>
        </div>
        {card.action === "Verify" ? (
          <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold text-white rounded-md bg-emerald-500">
            <Check size={9} /> Verify
          </span>
        ) : (
          <span
            className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-md"
            style={{ background: "rgba(0,157,185,0.12)", color: BRAND_DARK }}
          >
            Move <ArrowRight size={9} />
          </span>
        )}
      </div>
    </div>
  );
}

function AppFrame({ title, children, className = "" }) {
  return (
    <div
      className={`overflow-hidden bg-white border rounded-xl border-slate-300 ${className}`}
      style={{ boxShadow: "0 40px 90px -30px rgba(0,0,0,0.55), 0 8px 24px rgba(6,26,33,0.12)" }}
    >
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-slate-100">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <span className="flex-1 mx-4 text-center">
          <span className="inline-block px-5 py-0.5 text-[10.5px] font-medium rounded-md text-slate-500 bg-white border border-slate-200">
            {title}
          </span>
        </span>
      </div>
      {children}
    </div>
  );
}

export function Waveform() {
  return (
    <div className="flex items-center h-8 gap-[3px]">
      {WAVE.map((h, i) => (
        <span
          key={i}
          className={`flex-1 rounded-full iz-wave ${i < 9 ? "" : "opacity-50"}`}
          style={{
            height: `${h}%`,
            background: i < 9 ? BRAND : "#cbd5e1",
            animationDelay: `${(i % 7) * 120}ms`,
          }}
        />
      ))}
    </div>
  );
}

/* The large, realistic pipeline screen shown in the hero. */
export function HeroMock() {
  const navMain = [
    { icon: LayoutDashboard, label: "Dashboard" },
    { icon: Building2, label: "Companies" },
    { icon: TrendingUp, label: "Pipeline", active: true },
    { icon: CheckCircle, label: "Tasks" },
    { icon: MessageSquare, label: "Interactions" },
  ];
  const navAdmin = [
    { icon: Archive, label: "Archived" },
    { icon: Users, label: "Users" },
    { icon: Settings, label: "Settings" },
  ];
  return (
    <div className="relative">
      <AppFrame title="izsales · Sales Pipeline">
        <div className="flex h-[460px] sm:h-[560px]">
          {/* Sidebar */}
          <aside className="flex-col flex-shrink-0 hidden w-[196px] border-r md:flex border-slate-200 bg-white">
            <div className="flex items-center gap-2.5 px-4 py-4 border-b border-slate-100">
              <img src={LOGO_SRC} alt="" className="w-auto h-7" />
              <div className="leading-tight">
                <div className="text-[12px] font-extrabold text-slate-900">IZSales</div>
                <div className="text-[8.5px] font-semibold tracking-widest uppercase text-slate-400">CRM</div>
              </div>
            </div>
            <div className="flex-1 p-3">
              <div className="px-2 pb-2 text-[9px] font-bold tracking-widest uppercase text-slate-400">Workspace</div>
              <div className="space-y-1">
                {navMain.map(({ icon: Icon, label, active }) => (
                  <div
                    key={label}
                    className={`flex items-center gap-2.5 px-2.5 py-2 text-[12px] font-medium rounded-lg ${
                      active ? "text-white" : "text-slate-500"
                    }`}
                    style={active ? { background: BRAND } : undefined}
                  >
                    <Icon size={14} /> {label}
                  </div>
                ))}
              </div>
              <div className="px-2 pt-5 pb-2 text-[9px] font-bold tracking-widest uppercase text-slate-400">Manage</div>
              <div className="space-y-1">
                {navAdmin.map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-2.5 px-2.5 py-2 text-[12px] font-medium rounded-lg text-slate-500"
                  >
                    <Icon size={14} /> {label}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2.5 p-3 border-t border-slate-100">
              <span
                className="flex items-center justify-center w-8 h-8 text-[10px] font-bold text-white rounded-full"
                style={{ background: INK }}
              >
                SR
              </span>
              <div className="leading-tight">
                <div className="text-[11px] font-semibold text-slate-800">Sales Representative</div>
                <div className="text-[9.5px] text-slate-400">Online</div>
              </div>
            </div>
          </aside>

          {/* Main */}
          <div className="flex-1 min-w-0 bg-slate-100">
            <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-slate-200">
              <div>
                <div className="text-[14px] font-bold text-slate-900">Sales Pipeline</div>
                <div className="text-[10.5px] text-slate-400">12 companies · KSh 2.9M open pipeline</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="items-center hidden gap-1.5 px-3 py-2 text-[11px] border rounded-lg lg:flex w-52 text-slate-400 bg-slate-50 border-slate-200">
                  <Search size={12} /> Search companies…
                </div>
                <span className="relative">
                  <Bell size={15} className="text-slate-400" />
                  <span className="absolute w-2 h-2 bg-red-500 border border-white rounded-full -top-0.5 -right-0.5" />
                </span>
                <span
                  className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold text-white rounded-lg"
                  style={{ background: BRAND }}
                >
                  <Plus size={12} /> Add company
                </span>
              </div>
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-2 px-5 py-2.5 bg-white border-b border-slate-200">
              <span className="flex items-center gap-1.5 px-2.5 py-1 text-[10.5px] font-semibold border rounded-md text-slate-600 border-slate-200">
                <Filter size={10} /> Filters
              </span>
              {["All sales representatives", "All industries", "Stalled only"].map((t) => (
                <span
                  key={t}
                  className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 text-[10.5px] font-medium rounded-md text-slate-500 bg-slate-50 border border-slate-200"
                >
                  {t} <ChevronDown size={10} />
                </span>
              ))}
              <span className="ml-auto text-[10.5px] text-slate-400">Board view</span>
            </div>

            <div className="flex gap-3 p-4 overflow-hidden">
              {BOARD.map((col, ci) => (
                <div key={col.name} className="w-[210px] flex-shrink-0">
                  <div className="flex items-center gap-2 px-1 pb-2.5">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: col.color }} />
                    <span className="flex-1 text-[11px] font-bold truncate text-slate-700">{col.name}</span>
                    <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-white border border-slate-200 text-slate-500">
                      {col.cards.length}
                    </span>
                  </div>
                  <div className="h-[3px] mb-2.5 rounded-full" style={{ background: col.color }} />
                  {col.cards.map((card, i) => (
                    <BoardCard key={card.co} card={card} index={ci + i} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </AppFrame>

      {/* Floating: deal closed */}
      <div className="absolute hidden xl:flex items-center gap-3 px-4 py-3 bg-white border shadow-xl -left-8 top-24 rounded-xl border-slate-200 iz-float">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600">
          <CheckCircle size={20} />
        </div>
        <div>
          <div className="text-[12px] font-bold text-slate-900">Deal closed: Won</div>
          <div className="text-[11px] text-slate-500">Coastal Freight · KSh 450,000</div>
        </div>
      </div>

      {/* Floating: call recording */}
      <div
        className="absolute hidden xl:block w-[260px] p-3.5 bg-white border shadow-xl -right-8 -bottom-10 rounded-xl border-slate-200 iz-float"
        style={{ animationDelay: "1.2s" }}
      >
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2 text-[11.5px] font-bold text-slate-900">
            <span className="flex items-center justify-center w-6 h-6 rounded-md bg-red-50 text-red-500">
              <Mic size={12} />
            </span>
            Call recording
          </div>
        </div>
        <Waveform />
        <div className="flex justify-between mt-1.5 text-[10px] text-slate-400">
          <span>00:48</span>
          <span>02:14</span>
        </div>
      </div>
    </div>
  );
}

export function VerifyMock() {
  return (
    <div className="w-full max-w-md mx-auto overflow-hidden bg-white border shadow-2xl rounded-xl border-slate-200">
      <div className="flex items-start justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <div className="text-[15px] font-bold text-slate-900">Verify Company</div>
          <div className="text-[11.5px] text-slate-500">
            Review the company and record your qualification decision.
          </div>
        </div>
        <X size={16} className="text-slate-300" />
      </div>
      <div className="p-5 space-y-4">
        <div className="p-3 border rounded-lg border-slate-200 bg-slate-50">
          <div className="text-[9.5px] font-bold tracking-wider uppercase text-slate-400">Company</div>
          <div className="text-[14px] font-semibold text-slate-900">Lakeview Hotels</div>
        </div>
        <div>
          <div className="mb-1.5 text-[12px] font-semibold text-slate-700">
            Research Note <span className="font-normal text-slate-400">(Optional)</span>
          </div>
          <div className="h-20 px-3 py-2.5 text-[12px] border rounded-lg border-slate-200 text-slate-400">
            Add any findings or notes from your research…
          </div>
        </div>
        <div>
          <div className="mb-1.5 text-[12px] font-semibold text-slate-700">Qualification Decision</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center justify-center gap-2 h-10 text-[13px] font-semibold border rounded-lg text-emerald-700 border-emerald-200 bg-emerald-50">
              <CheckCircle size={15} /> Qualified
            </div>
            <div className="flex items-center justify-center gap-2 h-10 text-[13px] font-semibold text-orange-700 border border-orange-200 rounded-lg bg-orange-50">
              <X size={15} /> Not Qualified
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DashboardMock() {
  const kpis = [
    { label: "Total revenue", value: "KSh 1.2M", hint: "from won deals", icon: TrendingUp },
    { label: "Active deals", value: "14", hint: "open opportunities", icon: Briefcase },
    { label: "Tasks today", value: "6", hint: "2 overdue", icon: Clock },
    { label: "Total companies", value: "48", hint: "leads and customers", icon: Users },
  ];
  const bars = [
    { c: "#8B5CF6", h: 38, n: "Prospecting" },
    { c: "#EC4899", h: 55, n: "Qualification" },
    { c: "#F59E0B", h: 72, n: "Discovery" },
    { c: "#10B981", h: 60, n: "Demo" },
    { c: "#3B82F6", h: 85, n: "Proposal" },
    { c: "#6366F1", h: 48, n: "Negotiation" },
    { c: "#059689", h: 92, n: "Closed" },
  ];
  const mix = [
    { l: "Calls", v: "38%", c: "#16a34a", bg: "#dcfce7", icon: Phone },
    { l: "Emails", v: "16%", c: "#0284c7", bg: "#e0f2fe", icon: Mail },
    { l: "Meetings", v: "12%", c: "#7c3aed", bg: "#ede9fe", icon: Calendar },
    { l: "Notes", v: "24%", c: "#d97706", bg: "#fef3c7", icon: FileText },
    { l: "Field work", v: "10%", c: "#db2777", bg: "#fce7f3", icon: MapPin },
  ];
  return (
    <AppFrame title="izsales · Dashboard">
      <div className="p-4 sm:p-5 bg-slate-100">
        <div className="mb-4">
          <div className="text-[15px] font-bold text-slate-900">Good morning</div>
          <div className="text-[11px] text-slate-500">
            You have 2 overdue tasks and 14 deals in the pipeline today.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {kpis.map(({ label, value, hint, icon: Icon }) => (
            <div key={label} className="p-3 bg-white border rounded-lg border-slate-200">
              <div className="flex items-center justify-between">
                <span className="text-[9.5px] font-bold tracking-wider uppercase text-slate-400">{label}</span>
                <Icon size={12} style={{ color: BRAND }} />
              </div>
              <div className="mt-1.5 text-[20px] font-extrabold text-slate-900">{value}</div>
              <div className="text-[10px] text-slate-400">{hint}</div>
            </div>
          ))}
        </div>
        <div className="grid gap-3 mt-3 lg:grid-cols-5">
          <div className="p-4 bg-white border rounded-lg border-slate-200 lg:col-span-3">
            <div className="text-[12px] font-bold text-slate-900">Pipeline value</div>
            <div className="mb-3 text-[10px] text-slate-400">By stage</div>
            <div className="flex items-end gap-2 h-32">
              {bars.map((b) => (
                <div key={b.n} className="flex flex-col items-center flex-1 h-full justify-end gap-1.5">
                  <div className="w-full rounded-t" style={{ height: `${b.h}%`, background: b.c }} />
                  <span className="text-[8px] text-slate-400 truncate max-w-full">{b.n}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="p-4 bg-white border rounded-lg border-slate-200 lg:col-span-2">
            <div className="mb-3 text-[12px] font-bold text-slate-900">Interactions</div>
            <div className="grid grid-cols-2 gap-2">
              {mix.map(({ l, v, c, bg, icon: Icon }) => (
                <div key={l} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50">
                  <span
                    className="flex items-center justify-center w-7 h-7 rounded-md"
                    style={{ background: bg, color: c }}
                  >
                    <Icon size={12} />
                  </span>
                  <div>
                    <div className="text-[9px] text-slate-400">{l}</div>
                    <div className="text-[12px] font-bold text-slate-900">{v}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppFrame>
  );
}

export function TimelineMock() {
  const items = [
    {
      icon: Phone,
      bg: "#dcfce7",
      color: "#16a34a",
      title: "Product demo discussion",
      body: "Discussed arranging a demo. The contact asked for available dates next week.",
      meta: "Logged by Amina · 10:42",
    },
    {
      icon: Mail,
      bg: "#e0f2fe",
      color: "#0284c7",
      title: "Proposal for 50 licences",
      body: "To: grace@example.com · CC: peter@example.com",
      meta: "Logged by Amina · 09:15",
      chip: "proposal.pdf",
    },
    {
      icon: Mic,
      bg: "#dcfce7",
      color: "#16a34a",
      title: "Follow-up call (recorded)",
      audio: true,
      meta: "Logged by Brian · Yesterday",
    },
    {
      icon: FileText,
      bg: "#fef3c7",
      color: "#d97706",
      title: "OFFICE VISIT - Potential office visit",
      body: "Prospect is interested in seeing the solution in action.",
      meta: "Logged by Brian · Mon",
      edited: true,
    },
  ];
  return (
    <AppFrame title="izsales · Company history">
      <div className="p-4 space-y-3 sm:p-5 bg-slate-100">
        {items.map((it) => (
          <div key={it.title} className="flex gap-3 p-3.5 bg-white border rounded-lg border-slate-200">
            <div
              className="flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-lg"
              style={{ background: it.bg, color: it.color }}
            >
              <it.icon size={15} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[12.5px] font-semibold text-slate-900">{it.title}</span>
                {it.edited && <span className="text-[10px] italic text-slate-400">· edited</span>}
              </div>
              {it.body && <div className="mt-1 text-[11.5px] leading-relaxed text-slate-500">{it.body}</div>}
              {it.chip && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 mt-2 text-[10.5px] font-medium border rounded-md text-slate-600 border-slate-200 bg-slate-50">
                  <Paperclip size={10} /> {it.chip}
                </span>
              )}
              {it.audio && (
                <div className="flex items-center gap-3 px-3 py-2 mt-2 border rounded-lg border-slate-200 bg-slate-50">
                  <span
                    className="flex items-center justify-center w-7 h-7 text-white rounded-full"
                    style={{ background: BRAND }}
                  >
                    <Play size={11} fill="white" />
                  </span>
                  <div className="flex-1">
                    <Waveform />
                  </div>
                  <span className="text-[10px] text-slate-400">02:14</span>
                </div>
              )}
              <div className="mt-1.5 text-[10px] text-slate-400">{it.meta}</div>
            </div>
          </div>
        ))}
      </div>
    </AppFrame>
  );
}

const COMPANY_ROWS = [
  { co: "Savanna Logistics", person: "Amina Otieno", industry: "Logistics", stage: "Prospecting", contacts: 3 },
  { co: "Nairobi Tech Hub", person: "Grace Wanjiru", industry: "Technology", stage: "Lead Qualification", contacts: 2 },
  { co: "Rift Valley Dairies", person: "Lucy Achieng", industry: "Agriculture", stage: "Discovery", contacts: 4 },
  { co: "Coastal Freight Ltd", person: "Hassan Ali", industry: "Shipping", stage: "Demo / Pitch", contacts: 2 },
  { co: "Summit Education", person: "David Kiptoo", industry: "Education", stage: "Proposal", contacts: 5 },
];

/* Companies list with the primary contact and number of contact persons. */
export function CompaniesMock() {
  return (
    <AppFrame title="izsales · Companies">
      <div className="bg-slate-100">
        <div className="flex items-center justify-between gap-3 px-5 py-3 bg-white border-b border-slate-200">
          <div>
            <div className="text-[14px] font-bold text-slate-900">Companies</div>
            <div className="text-[10.5px] text-slate-400">48 companies · 112 contact persons</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="items-center hidden gap-1.5 px-3 py-2 text-[11px] border rounded-lg sm:flex w-48 text-slate-400 bg-slate-50 border-slate-200">
              <Search size={12} /> Search companies…
            </div>
            <span
              className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold text-white rounded-lg"
              style={{ background: BRAND }}
            >
              <Plus size={12} /> Add company
            </span>
          </div>
        </div>
        <div className="p-4">
          <div className="overflow-hidden bg-white border rounded-lg border-slate-200">
            {COMPANY_ROWS.map((r, i) => {
              const av = AVATARS[i % AVATARS.length];
              return (
                <div key={r.co} className="flex items-center gap-3 px-4 py-3 border-b last:border-b-0 border-slate-100">
                  <div
                    className="flex items-center justify-center flex-shrink-0 text-[10.5px] font-bold rounded-md w-9 h-9"
                    style={{ background: av.bg, color: av.color }}
                  >
                    {r.co.substring(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold truncate text-slate-900">{r.co}</div>
                    <div className="text-[10.5px] truncate text-slate-400">{r.person} · primary contact</div>
                  </div>
                  <span className="hidden w-24 text-[11px] text-slate-500 sm:block">{r.industry}</span>
                  <span
                    className="items-center hidden px-2 py-0.5 text-[10px] font-semibold rounded-md md:inline-flex"
                    style={{ background: "rgba(0,157,185,0.12)", color: BRAND_DARK }}
                  >
                    {r.stage}
                  </span>
                  <span className="flex items-center gap-1 text-[10.5px] text-slate-500">
                    <Users size={11} className="text-slate-300" /> {r.contacts}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AppFrame>
  );
}

/* A compact slice of the pipeline board (three columns). */
export function PipelineMock() {
  return (
    <AppFrame title="izsales · Sales Pipeline">
      <div className="bg-slate-100">
        <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-slate-200">
          <div>
            <div className="text-[14px] font-bold text-slate-900">Sales Pipeline</div>
            <div className="text-[10.5px] text-slate-400">12 companies · KSh 2.9M open pipeline</div>
          </div>
          <span
            className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold text-white rounded-lg"
            style={{ background: BRAND }}
          >
            <Plus size={12} /> Add company
          </span>
        </div>
        <div className="flex gap-3 p-4 overflow-hidden">
          {BOARD.slice(1, 4).map((col, ci) => (
            <div key={col.name} className="w-[210px] flex-shrink-0">
              <div className="flex items-center gap-2 px-1 pb-2.5">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: col.color }} />
                <span className="flex-1 text-[11px] font-bold truncate text-slate-700">{col.name}</span>
                <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-white border border-slate-200 text-slate-500">
                  {col.cards.length}
                </span>
              </div>
              <div className="h-[3px] mb-2.5 rounded-full" style={{ background: col.color }} />
              {col.cards.slice(0, 2).map((card, i) => (
                <BoardCard key={card.co} card={card} index={ci + i} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </AppFrame>
  );
}

const TASK_ROWS = [
  { title: "Send proposal to Summit Education", co: "Summit Education", due: "Today, 2:00 PM", priority: "High", rep: "SR", done: false },
  { title: "Follow-up call with Coastal Freight", co: "Coastal Freight Ltd", due: "Today, 4:30 PM", priority: "High", rep: "JK", done: false },
  { title: "Book a demo for Kilimani Clinics", co: "Kilimani Clinics", due: "Tomorrow", priority: "Medium", rep: "SR", done: false },
  { title: "Verify Lakeview Hotels", co: "Lakeview Hotels", due: "Wednesday", priority: "Medium", rep: "MN", done: true },
  { title: "Update contact details", co: "Mara Insurance", due: "Friday", priority: "Low", rep: "JK", done: false },
];

const PRIORITY_STYLES = {
  High: "text-red-600 bg-red-50 border-red-200",
  Medium: "text-amber-700 bg-amber-50 border-amber-200",
  Low: "text-slate-600 bg-slate-50 border-slate-200",
};

/* Tasks and follow-ups with priority, due date and assignee. */
export function TasksMock() {
  return (
    <AppFrame title="izsales · Tasks">
      <div className="bg-slate-100">
        <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-slate-200">
          <div>
            <div className="text-[14px] font-bold text-slate-900">Tasks &amp; follow-ups</div>
            <div className="text-[10.5px] text-slate-400">5 tasks · 2 due today</div>
          </div>
          <span
            className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold text-white rounded-lg"
            style={{ background: BRAND }}
          >
            <Plus size={12} /> Add task
          </span>
        </div>
        <div className="flex gap-2 px-5 py-2.5 bg-white border-b border-slate-200">
          {["Today", "Upcoming", "Overdue", "Done"].map((t, i) => (
            <span
              key={t}
              className={`px-2.5 py-1 text-[10.5px] font-semibold rounded-md ${
                i === 0 ? "text-white" : "text-slate-500 bg-slate-50 border border-slate-200"
              }`}
              style={i === 0 ? { background: BRAND } : undefined}
            >
              {t}
            </span>
          ))}
        </div>
        <div className="p-4 space-y-2.5">
          {TASK_ROWS.map((t) => (
            <div key={t.title} className="flex items-center gap-3 p-3.5 bg-white border rounded-lg border-slate-200">
              <span
                className={`flex items-center justify-center flex-shrink-0 w-5 h-5 border-2 rounded-full ${
                  t.done ? "text-white border-transparent" : "text-transparent border-slate-300"
                }`}
                style={t.done ? { background: BRAND } : undefined}
              >
                <Check size={11} strokeWidth={3} />
              </span>
              <div className="flex-1 min-w-0">
                <div
                  className={`text-[12.5px] font-semibold truncate ${
                    t.done ? "line-through text-slate-400" : "text-slate-900"
                  }`}
                >
                  {t.title}
                </div>
                <div className="flex items-center gap-1.5 mt-0.5 text-[10.5px] text-slate-400">
                  <Calendar size={10} /> {t.due} · {t.co}
                </div>
              </div>
              <span
                className={`hidden sm:inline-block px-2 py-0.5 text-[10px] font-semibold border rounded-md ${PRIORITY_STYLES[t.priority]}`}
              >
                {t.priority}
              </span>
              <span
                className="flex items-center justify-center w-6 h-6 text-[8px] font-bold text-white rounded-full"
                style={{ background: BRAND }}
              >
                {t.rep}
              </span>
            </div>
          ))}
        </div>
      </div>
    </AppFrame>
  );
}

const CUSTOMER_COLUMNS = [
  {
    name: "Closed",
    color: "#059689",
    cards: [{ co: "Kilimani Clinics", person: "Joy Njeri", industry: "Healthcare", deal: "Closed · KSh 275,000" }],
  },
  {
    name: "Customer",
    color: BRAND,
    cards: [
      { co: "Coastal Freight Ltd", person: "Hassan Ali", industry: "Shipping", deal: "Won · KSh 450,000", customer: true },
      { co: "Summit Education", person: "David Kiptoo", industry: "Education", deal: "Won · KSh 640,000", customer: true },
    ],
  },
];

/* Pipeline view showing won companies that have moved on to the Customer stage. */
export function CustomerMock() {
  return (
    <AppFrame title="izsales · Sales Pipeline">
      <div className="bg-slate-100">
        <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-slate-200">
          <div>
            <div className="text-[14px] font-bold text-slate-900">Sales Pipeline</div>
            <div className="text-[10.5px] text-slate-400">12 companies · 2 customers</div>
          </div>
          <span
            className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold text-white rounded-lg"
            style={{ background: BRAND }}
          >
            <Plus size={12} /> Add company
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 p-4">
          {CUSTOMER_COLUMNS.map((col, ci) => (
            <div key={col.name}>
              <div className="flex items-center gap-2 px-1 pb-2.5">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: col.color }} />
                <span className="flex-1 text-[11px] font-bold truncate text-slate-700">{col.name}</span>
                <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-white border border-slate-200 text-slate-500">
                  {col.cards.length}
                </span>
              </div>
              <div className="h-[3px] mb-2.5 rounded-full" style={{ background: col.color }} />
              {col.cards.map((card, i) => {
                const av = AVATARS[(ci * 2 + i + 1) % AVATARS.length];
                return (
                  <div
                    key={card.co}
                    className="mb-2.5 overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200"
                  >
                    <div className="flex items-center gap-2 px-3 pt-3">
                      <div
                        className="flex items-center justify-center flex-shrink-0 text-[10.5px] font-bold rounded-md w-8 h-8"
                        style={{ background: av.bg, color: av.color }}
                      >
                        {card.co.substring(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-semibold truncate text-slate-900">{card.co}</div>
                        <div className="text-[10px] truncate text-slate-400">{card.person}</div>
                      </div>
                    </div>
                    <div className="px-3 pt-2.5 space-y-1.5">
                      <div className="flex items-center gap-1.5 text-[10.5px] text-slate-500">
                        <Briefcase size={10} className="text-slate-300" /> {card.industry}
                      </div>
                      <div className="text-[10px] font-semibold text-slate-500">› {card.deal}</div>
                    </div>
                    <div className="flex items-center justify-end px-3 py-2 mt-2.5 border-t border-slate-100 bg-slate-50">
                      {card.customer ? (
                        <span
                          className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-md"
                          style={{ background: "rgba(0,157,185,0.12)", color: BRAND_DARK }}
                        >
                          <Check size={9} /> Customer
                        </span>
                      ) : (
                        <span
                          className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-md"
                          style={{ background: "rgba(0,157,185,0.12)", color: BRAND_DARK }}
                        >
                          Move <ArrowRight size={9} />
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </AppFrame>
  );
}